// Initiative Roadmap (BETA) — the task forces' roadmap, v2, tabbed.
//
// Always visible: health totals + filters (team / person / status / find,
// view-by, sort). Below them ONE surface at a time, chosen by tab (the owner's
// 2026-09-03 ask — v2 stacked everything and took three screens of scrolling):
//   Initiatives (current-state table, grouped by team or person on request;
//   click a row for the drill-down drawer) · Timeline (team-coloured Gantt) ·
//   Mind map (teams → initiatives → sub-actions, PJ's 2026-09-09 ask) ·
//   Blocked (blocked-by strip) · Merlin's questions (clarifications, "isn't
//   sure" list, and the integrity gaps Merlin will ask about next run) · What
//   changed (run-over-run history) · Reports (each run's full report opens in
//   a new tab). Tabs are the shared components/common/Tabs; the active tab
//   lives in the URL hash so a view can be linked (see roadmap/roadmapTabs.ts).
// Data: roadmap_initiatives current state (+ health/people/teams from the
// API), /admin/roadmap/events, /admin/roadmap/questions, /admin/roadmap/reports.

import { useCallback, useEffect, useMemo, useState, type ReactNode, useRef } from 'react';
import {
  roadmapReportsApi,
  type ClarifyingQuestionsResponse,
  type InitiativeState,
  type IntegrityReport,
  type RoadmapSourceListResponse,
  type OrgChartResponse,
  type RoadmapPersonRequest,
  type QuestionIdentity,
  type QuestionResponseRequest,
  type RoadmapEvent,
  type RoadmapOverview,
  type RoadmapReportListParams,
  type RoadmapRun,
  type RelevanceSummary,
  type RelevanceVerdict,
  type SourceRef,
  type TeamMandateListResponse,
  type TeamMandateRequest,
} from '../services/roadmapReports';
import type { SignalReport } from '../services/signalsReports';
import { myRoadmapApi } from '../services/myRoadmap';
import { useAuth } from '../context/AuthContext';
import { roadmapAccess } from './roadmap/roadmapAccess';
import { TimelineChart, type ModalSection } from './roadmap/TimelineChart';
import { RoadmapControls } from './roadmap/RoadmapControls';
import { RoadmapMindMap } from './roadmap/RoadmapMindMap';
import { BlockedStrip } from './roadmap/BlockedStrip';
import { ParkedPanel } from './roadmap/ParkedPanel';
import { ClarificationsPanel } from './roadmap/ClarificationsPanel';
import { RunHistoryPanel } from './roadmap/RunHistoryPanel';
import { SourcesPanel } from './roadmap/SourcesPanel';
import { OrgChartPanel } from './roadmap/OrgChartPanel';
import { MandatesPanel } from './roadmap/MandatesPanel';
import { TeamBriefBanner } from './roadmap/TeamBriefBanner';
import { RunStatusBanner } from './roadmap/RunStatusBanner';
import { describeRun } from './roadmap/roadmapRunStatus';
import { markIntroSeen } from './roadmap/roadmapIntro';
import { InitiativeTable } from './roadmap/InitiativeTable';
import { SubActionsTable } from './roadmap/SubActionsTable';
import { InitiativeModal } from './roadmap/InitiativeModal';
import { QuestionModal } from './roadmap/QuestionModal';
import type { OpenQuestionFn } from './roadmap/QuestionCard';
import { ActionModal } from './roadmap/ActionModal';
import { isoDay } from './roadmap/roadmapTimelineUtils';
import { SourceViewer } from './roadmap/SourceViewer';
import { parseSourceParam } from './roadmap/evidenceUtils';
import { IntegrityPanel } from './roadmap/IntegrityPanel';
import { TabPanel, Tabs } from '../components/common/Tabs';
import { RoadmapNav } from './roadmap/RoadmapNav';
import { MeetingPanel } from './roadmap/MeetingPanel';
import { agendaForSeries, defaultSeries, sameTeams, seriesById, seriesForTeams, type MeetingSeries, type SeriesId } from './roadmap/meetingSeries';
import { WeekOverviewPanel } from './roadmap/WeekOverviewPanel';
import { FILTERED_TABS, GROUPED_TABS, LEADERSHIP_TABS, TEAM_BRIEF_TABS, tabCounts, tabFromHash, useHashTab } from './roadmap/roadmapTabs';
import { useHashFilters } from './roadmap/roadmapHash';
import {
  anyFilterOn,
  applyFilters,
  isAddressedToMe,
  filterQuestionsResponse,
  hiddenQuestionCounts,
  questionCount,
  assignTeamColors,
  distinctPeople,
  distinctTeams,
  flattenGroups,
  groupInitiatives,
  mandateLookup,
  parkedBucket,
  timelineSet,
  sortInitiatives,
  type RoadmapFilters,
  type RoadmapMe,
  type SortKey,
} from './roadmap/roadmapViewUtils';
import styles from './SignalsReportsPage.module.css';
import tabStyles from './roadmap/RoadmapV2.module.css';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatDay(iso: string): { day: string; mon: string } {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return { day: '–', mon: '' };
  return { day: String(d.getUTCDate()), mon: MONTHS[d.getUTCMonth()] ?? '' };
}

function EmptyNote({ children, 'data-testid': testId }: { children: ReactNode; 'data-testid'?: string }) {
  return <div className={tabStyles.tabEmptyNote} data-testid={testId}>{children}</div>;
}

/** "1 question for the group and 2 unplaced notes" — whichever kinds are non-zero. */
function hiddenQuestionsLabel(h: { group: number; unplaced: number }): string {
  const parts: string[] = [];
  if (h.group > 0) parts.push(`${h.group} question${h.group === 1 ? '' : 's'} for the group`);
  if (h.unplaced > 0) parts.push(`${h.unplaced} unplaced note${h.unplaced === 1 ? '' : 's'}`);
  return parts.join(' and ');
}

/** The person's current directory email/leads: PUT /people replaces them, an org edit must keep them. */
async function currentLeads(name: string): Promise<{ email: string | null; leads: string[] }> {
  const people = await roadmapReportsApi.listPeople();
  const p = people.people.find((x) => x.name === name);
  return { email: p && p.email_source === 'directory' ? p.email : null, leads: p?.leads ?? [] };
}

const sameQuestion = (a: QuestionIdentity, b: QuestionIdentity) => a.initiative_key === b.initiative_key && a.question === b.question;
const questionIdentity = (q: QuestionIdentity): QuestionIdentity => ({ initiative_key: q.initiative_key, question: q.question });

/** A pop-up gets its own history entry: the browser's Back button closes it
 * instead of leaving the roadmap with it open. One entry per pop-up stack. */
function pushPopupHistory(): void {
  if (!(window.history.state && (window.history.state as { roadmapPopup?: boolean }).roadmapPopup)) {
    window.history.pushState({ roadmapPopup: true }, '', window.location.href);
  }
}

export function RoadmapReportsPage() {
  // One page for every door (roadmap/roadmapAccess.ts): admins run, answer
  // and edit; leadership (exec:portal) also reads the run reports and the
  // source documents; every other roadmap reader (roadmap:read, the portal
  // "My roadmap") gets the same views without those two, and answers only
  // the questions addressed to them through the my-roadmap endpoint.
  const { leadership, canRun, canEditTeams } = roadmapAccess(useAuth());
  const hiddenTabs = leadership ? undefined : LEADERSHIP_TABS;
  // Who the reader is on the roadmap (names + teams): the "Mine" filter.
  const [me, setMe] = useState<RoadmapMe | null>(null);
  const [items, setItems] = useState<SignalReport[]>([]);
  const [initiatives, setInitiatives] = useState<InitiativeState[]>([]);
  const [events, setEvents] = useState<RoadmapEvent[]>([]);
  const [questions, setQuestions] = useState<ClarifyingQuestionsResponse | null>(null);
  const [integrity, setIntegrity] = useState<IntegrityReport | null>(null);
  const [sources, setSources] = useState<RoadmapSourceListResponse | null>(null);
  const [org, setOrg] = useState<OrgChartResponse | null>(null);
  // v10 (kanban 838512f8): the task forces' mandates — the By-mandate grouping,
  // the teams editor's area select and the panel on the Org chart tab.
  const [mandates, setMandates] = useState<TeamMandateListResponse | null>(null);
  // Relevance marks (kanban 3709db93): the rate per run / team on the questions tab.
  const [relevanceSummary, setRelevanceSummary] = useState<RelevanceSummary | null>(null);
  // Last week → now (owner ask 2026-10-12): the earlier report beside the
  // latest; `sinceDays` picks how far back (0 = the run before).
  const [overview, setOverview] = useState<RoadmapOverview | null>(null);
  const [sinceDays, setSinceDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [runNote, setRunNote] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const [sort, setSort] = useState<SortKey>('attention');
  // The initiative pop-up: which initiative, which section to open on, and the
  // cross-link trail behind it (a link inside the pop-up pushes the current
  // initiative so Back returns to it; opening from a row or a glyph resets it).
  // `action` set = the pop-up is scoped to that one sub-action (kanban
  // 2a5883db); a cross-link or "open the whole initiative" drops it.
  const [selection, setSelection] = useState<{ key: string; section?: ModalSection; action?: string; stack: string[] } | null>(null);
  // The question pop-up (owner ask 2026-10-12): which of Merlin's questions,
  // and the list it was opened from (the meeting view's "Needs an answer
  // first", a person's list on the tab), so prev / next walk that list.
  const [askView, setAskView] = useState<{ id: QuestionIdentity; list: QuestionIdentity[] } | null>(null);
  // The source viewer (kanban 9eb5f005): the meeting / email a quote came
  // from, opened over the page (and over the pop-up). `?source=<kind>:<id>` in
  // the URL, the deep link the digest emails carry, opens it on load.
  const [sourceView, setSourceView] = useState<SourceRef | null>(() => (leadership ? parseSourceParam(window.location.search) : null));
  const openSourceFn = useCallback((ref: SourceRef) => setSourceView(ref), []);
  // The documents behind a quote are leadership-only: a reader sees the quote named, not linked.
  const openSource = leadership ? openSourceFn : undefined;
  const closeSource = useCallback(() => setSourceView(null), []);
  const [tab, setTab] = useHashTab(hiddenTabs);
  // The narrowing and grouping ride in the same hash ("#initiatives?team=Alpha+team&group=mandate"):
  // a meeting opens on its own list from a link (kanban 778cc5a0).
  const [filters, setFilters, viewBy, setViewBy, hashSeries, setHashSeries] = useHashFilters(tab);
  // Present: the roadmap fills the window, over the app's sidebar, header and
  // alert bar, for the wall screen in a meeting (both reviewers' first point:
  // the strongest thing on screen was a red alert that is not about the roadmap).
  const [presenting, setPresenting] = useState(false);
  // While presenting, the page itself is the scrolling box; otherwise the window is.
  const pageRef = useRef<HTMLDivElement | null>(null);
  const toTop = useCallback(() => {
    pageRef.current?.scrollTo?.(0, 0);
    window.scrollTo?.(0, 0);
  }, []);

  const [latestRun, setLatestRun] = useState<RoadmapRun | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  // The description is shown once per browser; after that the tabs start
  // above the fold (2026-09-27 assessment: the hero took half a laptop screen).
  // Closed unless asked for: on a wall the paragraph was three lines of chrome
  // above every view (it used to open once per browser).
  const [introOpen, setIntroOpen] = useState(false);
  const toggleIntro = useCallback(() => {
    setIntroOpen((v) => {
      if (v) markIntroSeen();
      return !v;
    });
  }, []);
  useEffect(() => {
    if (introOpen) markIntroSeen();
  }, [introOpen]);

  const runNow = useCallback(async () => {
    setRunning(true);
    setRunNote(null);
    try {
      const res = await roadmapReportsApi.run();
      setLatestRun(res.run);
      setNowMs(Date.now());
      // The banner carries the state from here; keep the note only when the
      // press joined a run that was already in flight.
      setRunNote(res.created ? null : res.detail);
    } catch {
      setRunNote('Could not queue the roadmap run — are you an admin, and is the worker up?');
    } finally {
      setRunning(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: RoadmapReportListParams = { limit: 100 };
      if (q.trim()) params.q = q.trim();
      // The run reports are leadership-only (the API answers 403 otherwise).
      const [reports, state] = await Promise.all([
        leadership ? roadmapReportsApi.list(params) : Promise.resolve({ items: [] as SignalReport[], total: 0, limit: 100, offset: 0 }),
        roadmapReportsApi.listInitiatives(),
      ]);
      setItems(reports.items);
      setInitiatives(state.items);
      // Secondary panels degrade independently — a failure there must not
      // blank the roadmap itself.
      const [ev, qs, ig, src, oc, md, rs, my] = await Promise.allSettled([
        roadmapReportsApi.listEvents(),
        roadmapReportsApi.listQuestions(),
        roadmapReportsApi.getIntegrity(),
        roadmapReportsApi.listSources({ limit: 500 }),
        roadmapReportsApi.getOrgChart(),
        roadmapReportsApi.listMandates(),
        roadmapReportsApi.getRelevanceSummary(),
        myRoadmapApi.get(),
      ]);
      setMe(my.status === 'fulfilled' ? { names: my.value.me, teams: my.value.teams ?? [] } : null);
      setEvents(ev.status === 'fulfilled' ? ev.value.events : []);
      setQuestions(qs.status === 'fulfilled' ? qs.value : null);
      setIntegrity(ig.status === 'fulfilled' ? ig.value : null);
      setSources(src.status === 'fulfilled' ? src.value : null);
      setOrg(oc.status === 'fulfilled' ? oc.value : null);
      setMandates(md.status === 'fulfilled' ? md.value : null);
      setRelevanceSummary(rs.status === 'fulfilled' ? rs.value : null);
    } catch (e) {
      // Named in the console so a failed load is diagnosable (kanban 01eaa27a).
      console.error('roadmap load failed', e);
      setError('Failed to load the roadmap.');
    } finally {
      setLoading(false);
    }
  }, [q, leadership]);

  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);
  // The overview re-reads on its own: when the window changes, and after each
  // load of the roadmap (a new run moves both sides). getOverview never throws.
  useEffect(() => {
    if (initiatives.length === 0) return undefined; // nothing to compare before the first run
    let cancelled = false;
    void roadmapReportsApi.getOverview(sinceDays).then((o) => { if (!cancelled) setOverview(o); });
    return () => { cancelled = true; };
  }, [sinceDays, initiatives]);

  // Answer / dismiss one of Merlin's questions (kanban 816b31c3): write, then
  // re-fetch ONLY the questions so the card flips to "waiting for Merlin". The
  // write's own error is left to the card (it shows it inline); a failed
  // re-fetch keeps what is on screen, like the other secondary panels.
  const refreshQuestions = useCallback(async () => {
    try {
      setQuestions(await roadmapReportsApi.listQuestions());
    } catch {
      // keep the current questions rather than blanking the tab
    }
  }, []);
  // An admin answers any question through the admin endpoint; every other
  // reader answers their own through /my-roadmap (the API refuses the rest).
  const respond = useCallback(
    async (req: QuestionResponseRequest) => {
      if (canRun) await roadmapReportsApi.respondToQuestion(req);
      else await myRoadmapApi.respond(req);
      await refreshQuestions();
    },
    [canRun, refreshQuestions],
  );
  // Org chart edits (kanban b9e6e6af): save, then re-read the chart.
  const editOrg = useCallback(async (name: string, patch: Partial<RoadmapPersonRequest>) => {
    const keep = await currentLeads(name);
    await roadmapReportsApi.setPerson(name, { ...keep, ...patch });
    setOrg(await roadmapReportsApi.getOrgChart());
  }, []);
  const withdraw = useCallback(
    async (id: QuestionIdentity) => {
      if (canRun) await roadmapReportsApi.withdrawResponse(id);
      else await myRoadmapApi.withdraw(id);
      await refreshQuestions();
    },
    [canRun, refreshQuestions],
  );
  // Who may answer what: admins everything; a reader the roadmap names, the
  // asks addressed to them; a reader it does not name, nothing.
  const canAnswer = canRun || (me !== null && me.names.length > 0);
  const mayRespond = useCallback(
    (question: { addressed_to?: string[]; people: string[] }) => canRun || (me !== null && isAddressedToMe(question, me)),
    [canRun, me],
  );
  const canRespondTo = canRun ? undefined : mayRespond;
  // Relevant / not relevant on a question (kanban 3709db93): write, then
  // re-read the questions (the card's counts) and the rate panel.
  const markRelevance = useCallback(
    async (id: QuestionIdentity, verdict: RelevanceVerdict | null) => {
      if (verdict === null) await roadmapReportsApi.clearRelevance(id);
      else await roadmapReportsApi.markRelevance({ ...id, verdict });
      await refreshQuestions();
      setRelevanceSummary(await roadmapReportsApi.getRelevanceSummary());
    },
    [refreshQuestions],
  );
  // An admin rewrote a team's mandate (kanban 838512f8): save, then re-read
  // the registry (counts, titles) and the initiatives (area titles follow).
  const saveMandate = useCallback(async (team: string, req: TeamMandateRequest) => {
    await roadmapReportsApi.setMandate(team, req);
    try {
      setMandates(await roadmapReportsApi.listMandates());
    } catch {
      // keep the current registry rather than blanking the panel
    }
    try {
      setInitiatives((await roadmapReportsApi.listInitiatives()).items);
    } catch {
      // the titles catch up on the next load
    }
  }, []);

  // An admin changed an initiative's teams in the drawer (kanban ae374351):
  // re-read the initiatives (mind map, team filters, table) and the change
  // feed, without the full-page loading state. The drawer already shows the
  // saved list; a failed re-read keeps what is on screen.
  const refreshInitiatives = useCallback(async () => {
    try {
      const state = await roadmapReportsApi.listInitiatives();
      setInitiatives(state.items);
    } catch {
      // keep the current initiatives rather than blanking the roadmap
    }
    try {
      setEvents((await roadmapReportsApi.listEvents()).events);
    } catch {
      // the feed degrades independently
    }
  }, []);

  // Run ledger: show the latest run, and while it is queued/running poll it —
  // reloading the roadmap the moment it publishes.
  const runActive = latestRun !== null && describeRun(latestRun, nowMs).active;
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const [newest] = await roadmapReportsApi.listRuns(1);
        if (cancelled) return;
        setNowMs(Date.now());
        setLatestRun((prev) => {
          if (prev && newest && prev.id === newest.id && prev.state !== 'published' && newest.state === 'published') {
            void load();
          }
          return newest ?? null;
        });
      } catch {
        // The ledger view degrades independently of the roadmap itself.
      }
    };
    void refresh();
    if (!runActive) return () => { cancelled = true; };
    const poll = setInterval(refresh, 10_000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [runActive, load]);

  const teams = useMemo(() => distinctTeams(initiatives), [initiatives]);
  const teamColors = useMemo(() => assignTeamColors(teams), [teams]);
  const people = useMemo(() => distinctPeople(initiatives), [initiatives]);
  const mandateIndex = useMemo(() => mandateLookup(mandates?.teams ?? []), [mandates]);
  // A registered task force (Sigma, before any initiative is its) is an owner
  // choice in the teams editor too.
  const knownTeams = useMemo(() => {
    const seen = new Map(teams.map((t) => [t.toLowerCase(), t]));
    for (const m of mandates?.teams ?? []) if (!seen.has(m.team.toLowerCase())) seen.set(m.team.toLowerCase(), m.team);
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [teams, mandates]);
  const groups = useMemo(
    () => groupInitiatives(sortInitiatives(applyFilters(initiatives, filters, me), sort), viewBy, teamColors, filters.teams, mandateIndex),
    [initiatives, filters, me, sort, viewBy, teamColors, mandateIndex],
  );
  const visible = useMemo(() => flattenGroups(groups), [groups]);
  const parked = useMemo(() => parkedBucket(initiatives, filters, me), [initiatives, filters, me]);
  // The questions tab follows the Mine / Team / Person / Find filters like every other
  // tab (group asks and unplaced notes have no team: see filterQuestionsResponse).
  const shownQuestions = useMemo(() => filterQuestionsResponse(questions, filters, me), [questions, filters, me]);
  const allQuestionCount = questionCount(questions);
  const shownQuestionCount = questionCount(shownQuestions);
  // Filters took every question off the tab: say so, instead of "no questions".
  const questionsFilteredOut = allQuestionCount > 0 && shownQuestionCount === 0;
  const hiddenQuestions = useMemo(() => hiddenQuestionCounts(questions, shownQuestions), [questions, shownQuestions]);
  // Empty only when there is nothing to read at all: answered asks, unplaced
  // notes and audit gaps still belong on the tab even when no ask is live.
  const questionsTabEmpty =
    shownQuestionCount === 0 && !(integrity && integrity.active_count > 0 && integrity.gaps.length > 0);
  // The meeting view: the agenda for ONE meeting series (the soonest by
  // weekday unless the room picked another), following the Mine / Team /
  // Person / Find filters like every other view (owner, 2026-10-12: "pretty
  // much everything on the page"); the whole roadmap is still passed so a
  // blocker outside the filter is named.
  const todayIso = new Date(nowMs).toISOString().slice(0, 10);
  // The series registry comes from the backend gate (leadership: all of
  // them, a reader: the meetings they are in); the page already holds the
  // roadmap, so series only.
  const [seriesList, setSeriesList] = useState<MeetingSeries[]>([]);
  useEffect(() => {
    let cancelled = false;
    myRoadmapApi.meetings(true).then((m) => { if (!cancelled) setSeriesList(m.series); }).catch(() => { /* the tab says so */ });
    return () => { cancelled = true; };
  }, []);
  // Which meeting (kanban 4d581b21: the agenda read differently with the
  // Alpha team filtered than with the Alpha Team meeting picked). The Team
  // filter and the meeting are ONE narrowing: a filter that is exactly a
  // series' team is that series; otherwise the pick the hash carries
  // (`series=`) stands, and with none, the soonest meeting by weekday.
  const pickedSeries = seriesList.find((s) => s.id === hashSeries) ?? null;
  const meetingSeries: SeriesId = seriesForTeams(seriesList, filters.teams)?.id ?? pickedSeries?.id ?? defaultSeries(seriesList, new Date(nowMs))?.id ?? '';
  // Picking a series pins it in the hash and sets the Team filter to its
  // team (none for the whole-roadmap meeting), so the Week, Projects and
  // List views follow the same team (owner, 2026-10-12: "pretty much
  // everything on the page" follows the filters).
  const pickSeries = useCallback((id: SeriesId) => {
    setHashSeries(id);
    const s = seriesList.find((x) => x.id === id);
    if (s && !sameTeams(filters.teams, s.teams)) setFilters({ ...filters, teams: [...s.teams] });
  }, [seriesList, filters, setFilters, setHashSeries]);
  // A Team chip that lands on exactly one series' team re-points a pinned
  // pick, so the hash never names one meeting while showing another.
  const changeFilters = useCallback((f: RoadmapFilters) => {
    setFilters(f);
    if (hashSeries && !sameTeams(f.teams, filters.teams)) {
      const s = seriesForTeams(seriesList, f.teams);
      if (s && s.id !== hashSeries) setHashSeries(s.id);
    }
  }, [seriesList, filters.teams, hashSeries, setFilters, setHashSeries]);
  const clearTeams = useCallback(() => setFilters({ ...filters, teams: [] }), [filters, setFilters]);
  // "See all N initiatives" promises the whole roadmap: the List opens with the
  // meeting's narrowing (its pick and the Team filter that came with it) taken off.
  const openWholeList = useCallback(() => {
    if (filters.teams.length > 0) setFilters({ ...filters, teams: [] });
    setHashSeries(null);
    setTab('initiatives');
    toTop();
  }, [filters, setFilters, setHashSeries, setTab, toTop]);
  const agenda = useMemo(() => {
    const series = seriesById(seriesList, meetingSeries);
    return series ? agendaForSeries({ series, initiatives: visible, all: initiatives, questions: shownQuestions?.items ?? [], today: new Date(nowMs) }) : null;
  }, [seriesList, meetingSeries, visible, initiatives, shownQuestions, nowMs]);
  // The questions due in the room, in agenda order, so the question pop-up's
  // prev / next walk the meeting (owner ask 2026-10-12: a question line opens
  // the pop-up, not the tab).
  const agendaQuestionIds = useMemo(
    () => (agenda ? agenda.items.flatMap((i) => i.dueQuestions.map((q) => ({ initiative_key: q.initiative_key, question: q.question }))) : []),
    [agenda],
  );
  const filtersOn = anyFilterOn(filters);
  const visibleKeys = useMemo(() => (filtersOn ? new Set(visible.map((i) => i.initiative_key)) : null), [filtersOn, visible]);
  const timeline = useMemo(() => timelineSet(visible, initiatives, filters, me), [visible, initiatives, filters, me]);
  const counts = useMemo(
    () =>
      tabCounts({
        visible, questions: shownQuestions, integrity, events, reports: items, sources, org, parked: parked.length, all: initiatives,
        meeting: agenda?.items.length ?? 0,
        week: overview ? overview.counts.set_out : 0,
        projects: overview ? overview.active : 0,
        timeline: timeline.items,
      }),
    [visible, shownQuestions, integrity, events, items, sources, org, parked, initiatives, agenda, timeline, overview],
  );
  const selected = selection
    ? initiatives.find((i) => i.initiative_key === selection.key) ?? null
    : null;
  const select = useCallback((key: string, section?: ModalSection, action?: string) => {
    pushPopupHistory();
    setAskView(null);
    setSelection({ key, section, action, stack: [] });
  }, []);
  useEffect(() => {
    const onPop = () => { setSelection(null); setAskView(null); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const openQuestion = useCallback((id: QuestionIdentity, list: QuestionIdentity[] = []) => {
    pushPopupHistory();
    setAskView({ id: questionIdentity(id), list: list.map(questionIdentity) });
  }, []);
  const openQuestionCard: OpenQuestionFn = useCallback((q, siblings) => openQuestion(q, siblings), [openQuestion]);
  const closeQuestion = useCallback(() => {
    setAskView(null);
    if (window.history.state && (window.history.state as { roadmapPopup?: boolean }).roadmapPopup) window.history.back();
  }, []);
  // The question shown is always the LIVE one from the questions read model,
  // so an answer sent from the pop-up flips it to "waiting for Merlin" in place.
  const askQuestion = useMemo(
    () => (askView && questions ? questions.items.find((q) => sameQuestion(q, askView.id)) ?? null : null),
    [askView, questions],
  );
  const askNav = useMemo(() => {
    if (!askView || askView.list.length < 2) return null;
    const index = askView.list.findIndex((x) => sameQuestion(x, askView.id));
    if (index < 0) return null;
    const go = (n: number) => setAskView({ id: askView.list[n], list: askView.list });
    return { index, total: askView.list.length, onPrev: () => go(index - 1), onNext: () => go(index + 1) };
  }, [askView]);
  // A cross-link from inside a pop-up: another initiative (with a back
  // trail), or one sub-action of the same initiative (no trail: the whole
  // initiative is one click away from the sub-action pop-up).
  const crossLink = useCallback(
    (key: string, section?: ModalSection, action?: string) =>
      setSelection((cur) => ({ key, section, action, stack: cur && cur.key !== key ? [...cur.stack, cur.key] : cur?.stack ?? [] })),
    [],
  );
  const back = useCallback(
    () =>
      setSelection((cur) => {
        if (!cur || cur.stack.length === 0) return cur;
        return { key: cur.stack[cur.stack.length - 1], section: 'summary', stack: cur.stack.slice(0, -1) };
      }),
    [],
  );
  const close = useCallback(() => {
    setSelection(null);
    // Leave the history entry the pop-up added, so Back is not pressed twice.
    if (window.history.state && (window.history.state as { roadmapPopup?: boolean }).roadmapPopup) window.history.back();
  }, []);
  const backTo = selection && selection.stack.length > 0
    ? (() => {
        const key = selection.stack[selection.stack.length - 1];
        return { key, title: initiatives.find((i) => i.initiative_key === key)?.title ?? key };
      })()
    : null;
  // The sub-action the pop-up is scoped to; a key that no longer exists (the
  // roadmap re-ran under the pop-up) falls back to the whole initiative.
  const selectedAction = selection?.action && selected
    ? selected.actions.find((a) => a.key === selection.action) ?? null
    : null;
  const openWholeInitiative = useCallback(() => crossLink(selection?.key ?? '', 'dates'), [crossLink, selection?.key]);
  const openSiblingAction = useCallback((actionKey: string) => crossLink(selection?.key ?? '', 'dates', actionKey), [crossLink, selection?.key]);
  const selectedQuestions = useMemo(
    () => (selection && questions ? questions.items.filter((q) => q.initiative_key === selection.key) : []),
    [selection, questions],
  );

  const initiativeTitles = useMemo(
    () => Object.fromEntries(initiatives.map((i) => [i.initiative_key, i.title])),
    [initiatives],
  );
  const hasRoadmap = !loading && !error && initiatives.length > 0;

  const reportsFeed = (
    <>
      <div className={styles.filters}>
        <input
          className={styles.search}
          placeholder="Search reports by title or summary…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="button" className={styles.reset} onClick={() => setQ('')}>
          Reset
        </button>
      </div>

      {!loading && !error && items.length === 0 && (
        <div className={styles.muted}>
          No roadmap reports yet — click “Run roadmap now” to generate the
          first one from the recent task-force meetings.
        </div>
      )}

      <div className={styles.feed}>
        {items.map((r) => {
          const d = formatDay(r.report_date);
          return (
            <a
              key={r.id}
              className={styles.card}
              href={roadmapReportsApi.viewPath(r.id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className={styles.dateBadge}>
                <span className={styles.day}>{d.day}</span>
                <span className={styles.mon}>{d.mon}</span>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.titleRow}>
                  <span className={styles.title}>{r.title}</span>
                  <span className={styles.type}>Roadmap</span>
                </div>
                <div className={styles.by}>
                  {r.author} · {new Date(r.created_at).toLocaleString()}
                </div>
                {r.summary && <div className={styles.summary}>{r.summary}</div>}
                <div className={styles.open}>Open full report ↗</div>
              </div>
            </a>
          );
        })}
      </div>
    </>
  );

  return (
    // The roadmap's own container (RoadmapV2.module.css .page): one width in
    // both the admin and the portal shell — see the note on that rule.
    <div ref={pageRef} className={`${tabStyles.page} ${presenting ? tabStyles.pagePresent : ''}`} data-presenting={presenting ? 'true' : undefined}>
      <header className={styles.head}>
        <div className={styles.headRow}>
          <div>
            <h1>
              Initiative Roadmap
              <button
                type="button"
                className={tabStyles.introToggle}
                onClick={toggleIntro}
                aria-expanded={introOpen}
                title={introOpen ? 'Hide the description' : 'What is this page?'}
              >
                {introOpen ? 'hide' : 'what is this?'}
              </button>
              {latestRun && latestRun.state === 'published' && (
                <span className={tabStyles.runLine} role="status" data-run-state="published">
                  {describeRun(latestRun, nowMs).detail.replace(/^Finished/, 'Updated').replace(/\.$/, '')}
                </span>
              )}
            </h1>
            {introOpen && (
              <p>
                What the Alpha Team / Delta Force task forces are working on, by
                team and by person: who owns what, what blocks what, target dates
                with on-time / late / stuck at a glance, what changed since the
                last run — and the questions Merlin still needs the group to
                answer. Pick a section below; each run&apos;s full report is under Reports.
              </p>
            )}
          </div>
          <button
            type="button"
            className={tabStyles.presentBtn}
            onClick={() => setPresenting((v) => !v)}
            aria-pressed={presenting}
            title={presenting ? 'Back to the normal page' : 'Fill the window with the roadmap: hides the menu and the alert bar, for a meeting screen'}
          >
            {presenting ? 'Exit presenting' : 'Present'}
          </button>
          {canRun && !presenting && (
            <button
              type="button"
              className={styles.runBtn}
              onClick={runNow}
              disabled={running || runActive}
              title="Queue an on-demand run of the roadmap agent — it reads the meetings, email and documents since its last run and the report lands in this feed when it finishes."
            >
              {running ? 'Queuing…' : 'Run roadmap now'}
            </button>
          )}
        </div>
        {/* A run in flight or a failed one keeps the full banner; a published
            one is a single quiet line beside the title (it was a green box
            taking a tenth of the screen to say nothing was wrong). */}
        {latestRun && latestRun.state !== 'published' && (
          <RunStatusBanner
            run={latestRun}
            nowMs={nowMs}
            retrying={running}
            onRetry={canRun ? runNow : undefined}
          />
        )}
        {runNote && <div className={styles.runNote}>{runNote}</div>}
      </header>

      {loading && <div className={styles.muted}>Loading…</div>}
      {error && <div className={styles.error}>{error}</div>}

      {hasRoadmap && (
        <>
          <RoadmapNav tab={tab} onTab={setTab} counts={counts} hidden={hiddenTabs} />
          {FILTERED_TABS.has(tab) && (
            <RoadmapControls
              teams={teams}
              teamColors={teamColors}
              people={people}
              filters={filters}
              onFilters={changeFilters}
              viewBy={viewBy}
              onViewBy={setViewBy}
              sort={sort}
              onSort={setSort}
              all={initiatives}
              shown={visible.length}
              grouping={GROUPED_TABS.has(tab)}
              tiles={tab !== 'questions' && tab !== 'meeting' && tab !== 'week' && tab !== 'projects'}
              me={me}
            />
          )}
          {/* What the selected team is for (kanban 16a22d06): its definition
              and areas from the registry, before the list of what it does. */}
          {TEAM_BRIEF_TABS.has(tab) && filters.teams.length > 0 && (
            <TeamBriefBanner teams={filters.teams} mandates={mandates?.teams ?? []} teamColors={teamColors} />
          )}
          <Tabs activeTab={tab} onTabChange={(t) => setTab(tabFromHash(t, hiddenTabs))}>
            {/* The Meeting section is three short views, not one scroll (owner, 2026-10-14). */}
            <TabPanel value="meeting">
              <MeetingPanel
                initiatives={visible}
                allInitiatives={initiatives}
                questions={shownQuestions?.items ?? []}
                today={todayIso}
                seriesList={seriesList}
                series={meetingSeries}
                onSeriesChange={pickSeries}
                filterTeams={filters.teams}
                onClearTeams={clearTeams}
                teamColors={teamColors}
                onSelect={select}
                onOpenQuestion={(id) => openQuestion(id, agendaQuestionIds)}
                onOpenQuestions={() => { setTab('questions'); toTop(); }}
                onOpenList={openWholeList}
                onOpenSource={openSource}
                onRespond={canAnswer ? respond : undefined}
                onWithdraw={canAnswer ? withdraw : undefined}
                canRespondTo={canRespondTo}
                asOf={questions?.report_date ?? null}
              />
            </TabPanel>

            <TabPanel value="week">
              {overview ? (
                <WeekOverviewPanel
                  data={overview}
                  visibleKeys={visibleKeys}
                  filtered={filtersOn}
                  initiatives={initiatives}
                  onSelect={select}
                  sinceDays={sinceDays}
                  onSinceDays={setSinceDays}
                  view="week"
                />
              ) : (
                <EmptyNote>Last week → now fills in once two roadmap reports exist to compare.</EmptyNote>
              )}
            </TabPanel>

            <TabPanel value="projects">
              {overview ? (
                <WeekOverviewPanel
                  data={overview}
                  visibleKeys={visibleKeys}
                  filtered={filtersOn}
                  initiatives={initiatives}
                  onSelect={select}
                  sinceDays={sinceDays}
                  onSinceDays={setSinceDays}
                  view="projects"
                />
              ) : (
                <EmptyNote>Where projects stand fills in after the first roadmap report with a structured payload.</EmptyNote>
              )}
            </TabPanel>

            <TabPanel value="initiatives">
              {viewBy === 'actions' ? (
                <SubActionsTable initiatives={visible} teamColors={teamColors} onSelect={select} />
              ) : (
                <InitiativeTable groups={groups} all={initiatives} teamColors={teamColors} onSelect={select} viewBy={viewBy} />
              )}
            </TabPanel>

            <TabPanel value="timeline">
              {counts.timeline === 0 ? (
                <EmptyNote>Nothing to plot — no initiatives match the current filters.</EmptyNote>
              ) : (
                <TimelineChart
                  initiatives={timeline.items}
                  retained={timeline.retained}
                  showDone={filters.includeClosed}
                  events={events}
                  teamColors={teamColors}
                  onSelect={select}
                  filterTeams={filters.teams}
                />
              )}
            </TabPanel>

            <TabPanel value="mindmap">
              {counts.mindmap === 0 ? (
                <EmptyNote>Nothing to map — no initiatives match the current filters.</EmptyNote>
              ) : (
                <RoadmapMindMap initiatives={visible} teamColors={teamColors} onSelect={select} mandates={mandateIndex} />
              )}
            </TabPanel>

            <TabPanel value="blocked">
              {counts.blocked === 0 ? (
                <EmptyNote>Nothing is blocked among the initiatives shown — no live dependency is holding work up.</EmptyNote>
              ) : (
                <BlockedStrip initiatives={visible} all={initiatives} onSelect={select} />
              )}
            </TabPanel>

            <TabPanel value="parked">
              {counts.parked === 0 ? (
                <EmptyNote>Nothing is parked among the initiatives shown. Sidelined or indefinitely blocked items land here.</EmptyNote>
              ) : (
                <ParkedPanel items={parked} teamColors={teamColors} onSelect={select} />
              )}
            </TabPanel>

            <TabPanel value="questions">
              {questionsFilteredOut && (
                <EmptyNote>
                  No questions match the current filters. Clear Team / Person / Find to see all {allQuestionCount}.
                </EmptyNote>
              )}
              {questionsTabEmpty ? (
                !questionsFilteredOut && (
                  <EmptyNote>Merlin has no open questions and the integrity audit found no gaps.</EmptyNote>
                )
              ) : (
                <>
                  {/* Answering / dismissing: admins any question (the admin
                      endpoint); a reader the roadmap names, their own (the
                      my-roadmap endpoint); other readers see them read-only. */}
                  <ClarificationsPanel
                    data={shownQuestions}
                    teamColors={teamColors}
                    onSelect={select}
                    initiatives={initiatives}
                    integrity={integrity}
                    onRespond={canAnswer ? respond : undefined}
                    onWithdraw={canAnswer ? withdraw : undefined}
                    canRespondTo={canRespondTo}
                    canEmail={canRun}
                    onlyPerson={filters.person}
                    onOpenSource={openSource}
                    onOpen={openQuestionCard}
                    onMark={markRelevance}
                    relevanceSummary={relevanceSummary}
                  />
                  {!questionsFilteredOut && hiddenQuestions.group + hiddenQuestions.unplaced > 0 && (
                    <EmptyNote data-testid="questions-hidden-note">
                      Hidden by the {filters.mine ? 'Mine' : 'Team / Person'} filter: {hiddenQuestionsLabel(hiddenQuestions)}. They belong to no team or person; clear the filter to see them.
                    </EmptyNote>
                  )}
                  <IntegrityPanel report={integrity} onSelect={select} />
                </>
              )}
            </TabPanel>

            <TabPanel value="org">
              <MandatesPanel data={mandates} onSave={canRun ? saveMandate : undefined} />
              {!org ? (
                <EmptyNote>The org chart could not be loaded.</EmptyNote>
              ) : (
                <OrgChartPanel data={org} onEdit={canRun ? editOrg : undefined} />
              )}
            </TabPanel>

            <TabPanel value="history">
              {counts.history === 0 ? (
                <EmptyNote>No run-over-run changes recorded yet — history appears after the second roadmap run.</EmptyNote>
              ) : (
                <RunHistoryPanel events={events} onSelect={select} reports={items} />
              )}
            </TabPanel>

            <TabPanel value="sources">
              {!sources || sources.total === 0 ? (
                <EmptyNote>No sources on the ledger yet — it fills from the next roadmap run.</EmptyNote>
              ) : (
                <SourcesPanel data={sources} onSelect={select} titles={initiativeTitles} />
              )}
            </TabPanel>

            {leadership && <TabPanel value="reports">{reportsFeed}</TabPanel>}
          </Tabs>
        </>
      )}

      {/* Before the first run there is no roadmap to tab — show the feed (and
          its "run it" empty state) directly, as v2 did; a reader without the
          feed gets a plain note. */}
      {!hasRoadmap && !loading && (leadership ? reportsFeed : !error && (
        <EmptyNote data-testid="roadmap-not-yet">No roadmap yet — it appears here after the first roadmap run.</EmptyNote>
      ))}

      {selected && selection && selectedAction && (
        <ActionModal
          initiative={selected}
          action={selectedAction}
          teamColors={teamColors}
          onOpenInitiative={openWholeInitiative}
          onOpenAction={openSiblingAction}
          onClose={close}
          sourceLedger={sources?.items ?? []}
          onOpenSource={openSource}
          today={isoDay(new Date())}
        />
      )}
      {selected && selection && !selectedAction && (
        <InitiativeModal
          initiative={selected}
          all={initiatives}
          teamColors={teamColors}
          onSelect={crossLink}
          onClose={close}
          backTo={backTo}
          onBack={back}
          initialSection={selection.section}
          sourceLedger={sources?.items ?? []}
          questions={selectedQuestions}
          onRespond={canAnswer ? respond : undefined}
          onWithdraw={canAnswer ? withdraw : undefined}
          canRespondTo={canRespondTo}
          knownTeams={knownTeams}
          canEditTeams={canEditTeams}
          canPark={canRun}
          onTeamsChanged={refreshInitiatives}
          onOpenSource={openSource}
          mandates={mandateIndex}
        />
      )}

      {askQuestion && !selection && (
        <QuestionModal
          question={askQuestion}
          initiative={initiatives.find((i) => i.initiative_key === askQuestion.initiative_key) ?? null}
          reportDate={questions?.report_date ?? null}
          onClose={closeQuestion}
          onSelect={select}
          onRespond={mayRespond(askQuestion) ? respond : undefined}
          onWithdraw={mayRespond(askQuestion) ? withdraw : undefined}
          onOpenSource={openSource}
          onMark={markRelevance}
          nav={askNav}
          // Not closeQuestion(): its history.back() is asynchronous and would
          // race the tab's own pushState; the pop-up's entry stays underneath,
          // so Back from the tab lands on the meeting view.
          onOpenAll={() => { setAskView(null); setTab('questions'); toTop(); }}
        />
      )}

      {leadership && sourceView && <SourceViewer source={sourceView} onClose={closeSource} />}
    </div>
  );
}
